import { useState } from 'react';
import type { BoardroomObservation } from '../../../types';

const CHIPS_SHOWN = 6;

/**
 * What the observer noticed looking over the whole board.
 *
 * Two kinds, kept apart because they ask different things of the reader: a
 * FINDING is something the board adds up to ("no standalone tool is priced
 * between $45 and $149"); a DATA PROBLEM is something on the board that is
 * wrong or too thin to trust (a product under the wrong maker, the same thing
 * twice). Every one names the items it rests on, as chips that open them, so
 * the reader can check it rather than take it on trust.
 *
 * "Seen" marks it done; it leaves the tray and the observer is told it was seen.
 */
export default function ObservationTray({
  observations,
  busy,
  onOpen,
  onOpenPage,
  onSeen,
}: {
  observations: BoardroomObservation[];
  busy?: boolean;
  onOpen: (itemId: number) => void;
  /** The observation's own page: its full text and every item it rests on.
   *  A card shows six of those; the page shows them all. */
  onOpenPage?: (itemId: number) => void;
  onSeen: (o: BoardroomObservation) => void;
}) {
  const findings = observations.filter(o => o.sort === 'finding');
  const problems = observations.filter(o => o.sort !== 'finding');
  const [showing, setShowing] = useState<'findings' | 'problems'>(
    findings.length || !problems.length ? 'findings' : 'problems',
  );
  if (!observations.length) return null;
  const shown = showing === 'findings' ? findings : problems;
  const tab = (key: 'findings' | 'problems', label: string, n: number) => (
    <button
      type="button"
      onClick={() => setShowing(key)}
      aria-pressed={showing === key}
      data-testid={`observations-${key}`}
      className={`rounded px-1.5 py-0.5 text-[11px] ${
        showing === key
          ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
          : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label} ({n})
    </button>
  );
  return (
    <div className="shrink-0" data-testid="observation-tray">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Observed
        </span>
        {tab('findings', 'Findings', findings.length)}
        {tab('problems', 'Data problems', problems.length)}
      </div>
      {!shown.length ? (
        <div className="pb-1 text-xs text-gray-400 dark:text-gray-500">Nothing here.</div>
      ) : (
        <div className="flex flex-wrap gap-2 pb-1">
          {shown.map(o => {
            const problem = o.sort !== 'finding';
            return (
              <div
                key={o.id}
                data-testid={`observation-${o.id}`}
                className={`min-w-[16rem] max-w-[22rem] shrink-0 rounded-lg border px-2.5 py-2 ${
                  problem
                    ? 'border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/20'
                    : 'border-sky-200 bg-sky-50/60 dark:border-sky-800 dark:bg-sky-900/20'
                }`}
              >
                <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                  {o.sort === 'duplicate' && (
                    <span className="mr-1 text-[11px] uppercase text-amber-700 dark:text-amber-300">
                      Duplicate
                    </span>
                  )}
                  {onOpenPage ? (
                    <button
                      type="button"
                      onClick={() => onOpenPage(o.id)}
                      className="text-left hover:underline"
                      title="Open it: the whole of it, and everything it rests on"
                      data-testid={`observation-open-${o.id}`}
                    >
                      {o.title}
                    </button>
                  ) : (
                    o.title
                  )}
                </div>
                {o.body && (
                  <div className="mt-0.5 line-clamp-3 text-xs text-gray-600 dark:text-gray-300" title={o.body}>
                    {o.body}
                  </div>
                )}
                {o.rows_counted != null && (
                  <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    Counted over {o.rows_counted} row{o.rows_counted === 1 ? '' : 's'}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {o.cites.slice(0, CHIPS_SHOWN).map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onOpen(c.id)}
                      title={`${c.kind.toLowerCase()} #${c.id}`}
                      className="max-w-[10rem] truncate rounded border border-gray-300 bg-white/70 px-1 py-0.5 text-[11px] text-gray-700 hover:bg-white dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {c.title}
                    </button>
                  ))}
                  {/* THE REST ARE ON ITS PAGE. "+5 more" used to be words you
                      could not press, and the card has no room to list them. */}
                  {o.cites.length > CHIPS_SHOWN &&
                    (onOpenPage ? (
                      <button
                        type="button"
                        onClick={() => onOpenPage(o.id)}
                        className="px-1 py-0.5 text-[11px] text-indigo-600 hover:underline dark:text-indigo-300"
                        title={`See all ${o.cites.length} it rests on`}
                        data-testid={`observation-more-${o.id}`}
                      >
                        +{o.cites.length - CHIPS_SHOWN} more
                      </button>
                    ) : (
                      <span className="px-1 py-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        +{o.cites.length - CHIPS_SHOWN} more
                      </span>
                    ))}
                </div>
                {o.question && (
                  <button
                    type="button"
                    onClick={() => onOpen(o.question!.id)}
                    className="mt-1 block text-left text-xs text-gray-600 hover:underline dark:text-gray-300"
                  >
                    Asked the room: {o.question.title}
                  </button>
                )}
                <div className="mt-1.5">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSeen(o)}
                    className="text-xs text-gray-500 hover:underline disabled:opacity-50 dark:text-gray-400"
                  >
                    Seen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
